import {
    Box,
    Button,
    FormControlLabel,
    FormLabel,
    Option,
    Radio,
    RadioGroup,
    SelectField,
    TextField,
    Typography,
} from '@deere/fuel-react';
import { useEffect, useMemo, useState } from 'react';
import styles from './SupportRequestForm.module.css';

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
const REQUIRED_ERROR_MESSAGE = 'This field is required.';
const DESCRIPTION_HELPER_TEXT =
    'Please do not include confidential information (e.g. birth date, SSN).';
const GENERIC_MIME_TYPES = new Set(['', 'application/octet-stream']);

const CATEGORY_OPTIONS = ['Access', 'Bidding', 'Performance', 'Data', 'Other'];
const PRIORITY_OPTIONS = ['Medium', 'Low'];
const ALLOWED_EXTENSIONS = new Set([
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.pdf',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.bmp',
    '.webp',
    '.tiff',
    '.mp4',
    '.mov',
    '.avi',
    '.wmv',
    '.mkv',
    '.webm',
    '.mp3',
    '.wav',
    '.m4a',
    '.aac',
    '.ogg',
]);

const ACCEPT_ATTR = Array.from(ALLOWED_EXTENSIONS).join(',');

const EXTENSION_MIME_FAMILY = {
    '.doc': 'word',
    '.docx': 'word',
    '.xls': 'excel',
    '.xlsx': 'excel',
    '.ppt': 'powerpoint',
    '.pptx': 'powerpoint',
    '.pdf': 'pdf',
    '.png': 'image',
    '.jpg': 'image',
    '.jpeg': 'image',
    '.gif': 'image',
    '.bmp': 'image',
    '.webp': 'image',
    '.tiff': 'image',
    '.mp4': 'video',
    '.mov': 'video',
    '.avi': 'video',
    '.wmv': 'video',
    '.mkv': 'video',
    '.webm': 'video',
    '.mp3': 'audio',
    '.wav': 'audio',
    '.m4a': 'audio',
    '.aac': 'audio',
    '.ogg': 'audio',
};

const getExtension = (fileName = '') => {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex < 0) {
        return '';
    }
    return fileName.substring(dotIndex).toLowerCase();
};

const getMimeFamily = (mimeType = '') => {
    const normalizedMime = mimeType.toLowerCase().trim();
    if (GENERIC_MIME_TYPES.has(normalizedMime)) {
        return '';
    }

    if (normalizedMime.startsWith('image/')) {
        return 'image';
    }

    if (normalizedMime.startsWith('video/')) {
        return 'video';
    }

    if (normalizedMime.startsWith('audio/')) {
        return 'audio';
    }

    if (
        normalizedMime === 'application/msword' ||
        normalizedMime ===
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
        return 'word';
    }

    if (
        normalizedMime === 'application/vnd.ms-excel' ||
        normalizedMime ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
        return 'excel';
    }

    if (
        normalizedMime === 'application/vnd.ms-powerpoint' ||
        normalizedMime ===
            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
        return 'powerpoint';
    }

    if (
        normalizedMime === 'application/pdf' ||
        normalizedMime === 'application/x-pdf'
    ) {
        return 'pdf';
    }

    if (normalizedMime === 'application/ogg') {
        return 'audio';
    }

    return '';
};

const doesMimeMatchExtension = (extension, mimeType = '') => {
    const expectedFamily = EXTENSION_MIME_FAMILY[extension] || '';
    const mimeFamily = getMimeFamily(mimeType);

    if (!expectedFamily || !mimeFamily) {
        return true;
    }

    return expectedFamily === mimeFamily;
};

const toTrimmedValue = (value) =>
    typeof value === 'string' ? value.trim() : '';

const toUniqueUnitOptions = (unitOptions = []) =>
    Array.from(new Set(unitOptions.map(toTrimmedValue).filter(Boolean)));

export const SupportRequestForm = ({
    onSubmit,
    prefillUnit,
    unitOptions = [],
    submitting,
}) => {
    const safePrefillUnit = prefillUnit || '';
    const safeUnitOptions = useMemo(
        () => toUniqueUnitOptions(unitOptions),
        [unitOptions]
    );

    const initialUnit = useMemo(() => {
        if (safeUnitOptions.length > 1) {
            return safeUnitOptions.includes(safePrefillUnit)
                ? safePrefillUnit
                : '';
        }

        return safePrefillUnit || safeUnitOptions[0] || '';
    }, [safePrefillUnit, safeUnitOptions]);

    const [category, setCategory] = useState('');
    const [unit, setUnit] = useState(initialUnit);
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('');
    const [files, setFiles] = useState([]);

    const [categoryTouched, setCategoryTouched] = useState(false);
    const [unitTouched, setUnitTouched] = useState(false);
    const [descriptionTouched, setDescriptionTouched] = useState(false);
    const [priorityTouched, setPriorityTouched] = useState(false);
    const [fileErrorMessage, setFileErrorMessage] = useState('');

    useEffect(() => {
        setUnit(initialUnit);
    }, [initialUnit]);

    const isCategoryInvalid = categoryTouched && !category;
    const isUnitInvalid = unitTouched && !toTrimmedValue(unit);
    const isDescriptionInvalid =
        descriptionTouched && !toTrimmedValue(description);
    const isPriorityInvalid = priorityTouched && !priority;

    const attachmentNames = useMemo(
        () => files.map((file) => file.name).join(', '),
        [files]
    );

    const validateFiles = (selectedFiles) => {
        if (selectedFiles.length > MAX_ATTACHMENTS) {
            return {
                validFiles: [],
                errorMessage: `You can attach up to ${MAX_ATTACHMENTS} files.`,
            };
        }

        const validFiles = [];
        const fileErrors = [];

        selectedFiles.forEach((file) => {
            if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
                fileErrors.push(`${file.name}: file size exceeds 10 MB limit.`);
                return;
            }

            const extension = getExtension(file.name);
            if (!ALLOWED_EXTENSIONS.has(extension)) {
                fileErrors.push(`${file.name}: file type is not allowed.`);
                return;
            }

            if (!doesMimeMatchExtension(extension, file.type)) {
                fileErrors.push(
                    `${file.name}: file type does not match its extension.`
                );
                return;
            }

            validFiles.push(file);
        });

        return {
            validFiles,
            errorMessage: fileErrors.join(' '),
        };
    };

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        const { validFiles, errorMessage } = validateFiles(selectedFiles);
        setFiles(validFiles);
        setFileErrorMessage(errorMessage);
    };

    const validateForm = () => {
        setCategoryTouched(true);
        setUnitTouched(true);
        setDescriptionTouched(true);
        setPriorityTouched(true);

        return (
            Boolean(category) &&
            Boolean(toTrimmedValue(unit)) &&
            Boolean(toTrimmedValue(description)) &&
            Boolean(priority)
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }

        if (typeof onSubmit === 'function') {
            onSubmit(
                {
                    category,
                    unit: toTrimmedValue(unit),
                    description: toTrimmedValue(description),
                    priority,
                },
                files
            );
        }
    };

    const renderUnitField = () => {
        if (safeUnitOptions.length > 1) {
            return (
                <SelectField
                    label="John Deere Location / Unit"
                    aria-label="John Deere Location / Unit"
                    value={unit}
                    onChange={(event) => {
                        setUnitTouched(true);
                        setUnit(event.target.value);
                    }}
                    error={isUnitInvalid}
                    helperText={isUnitInvalid ? REQUIRED_ERROR_MESSAGE : ''}
                >
                    <Option disabled hidden value="">
                        Select a John Deere Location / Unit
                    </Option>
                    {safeUnitOptions.map((option) => (
                        <Option key={option} value={option}>
                            {option}
                        </Option>
                    ))}
                </SelectField>
            );
        }

        return (
            <div data-testid="support-unit-field">
                <FormLabel>John Deere Location / Unit</FormLabel>
                <Typography data-testid="support-unit-value">{unit}</Typography>
                {isUnitInvalid && (
                    <Typography color="error.main">
                        {REQUIRED_ERROR_MESSAGE}
                    </Typography>
                )}
            </div>
        );
    };

    return (
        <Box component="div">
            <form
                onSubmit={handleSubmit}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
            >
                <div
                    data-testid="support-category-field"
                    className={styles.formField}
                >
                    <SelectField
                        required
                        label="Job Bids Application Category"
                        aria-label="Job Bids Application Category"
                        value={category}
                        onChange={(event) => {
                            setCategoryTouched(true);
                            setCategory(event.target.value);
                        }}
                        error={isCategoryInvalid}
                        helperText={
                            isCategoryInvalid ? REQUIRED_ERROR_MESSAGE : ''
                        }
                        className={styles.categoryField}
                    >
                        <Option disabled hidden value="">
                            Select a category
                        </Option>
                        {CATEGORY_OPTIONS.map((option) => (
                            <Option key={option} value={option}>
                                {option}
                            </Option>
                        ))}
                    </SelectField>
                </div>

                {renderUnitField()}

                <TextField
                    required
                    label="Issue Description"
                    multiline
                    minRows={3}
                    value={description}
                    onChange={(event) => {
                        setDescriptionTouched(true);
                        setDescription(event.target.value);
                    }}
                    helperText={
                        isDescriptionInvalid
                            ? `${REQUIRED_ERROR_MESSAGE} ${DESCRIPTION_HELPER_TEXT}`
                            : DESCRIPTION_HELPER_TEXT
                    }
                    error={isDescriptionInvalid}
                />

                <FormLabel required error={isPriorityInvalid}>
                    Priority
                </FormLabel>
                <RadioGroup
                    aria-label="Priority"
                    aria-describedby={
                        isPriorityInvalid
                            ? 'support-request-priority-error'
                            : undefined
                    }
                    name="Priority"
                    value={priority}
                    error={isPriorityInvalid ? 'invalid' : null}
                    onChange={(event) => {
                        setPriorityTouched(true);
                        setPriority(event.target.value);
                    }}
                >
                    {PRIORITY_OPTIONS.map((option) => (
                        <FormControlLabel
                            key={option}
                            control={<Radio />}
                            label={option}
                            value={option}
                        />
                    ))}
                </RadioGroup>
                {isPriorityInvalid && (
                    <Typography
                        id="support-request-priority-error"
                        color="error.main"
                    >
                        {REQUIRED_ERROR_MESSAGE}
                    </Typography>
                )}

                <FormLabel>Attachments</FormLabel>
                <input
                    type="file"
                    multiple
                    accept={ACCEPT_ATTR}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="support-request-file-input"
                />
                <label htmlFor="support-request-file-input">
                    <Button component="span" variant="secondary">
                        {files.length > 0
                            ? 'Replace selected files'
                            : 'Add files'}
                    </Button>
                </label>
                <Typography>
                    Do not include confidential information. Up to 10 files, 10
                    MB each. Allowed: Word, Excel, PPT, PDF, Image, Video,
                    Audio.
                </Typography>
                {attachmentNames && <Typography>{attachmentNames}</Typography>}
                {fileErrorMessage && (
                    <Typography color="error.main">
                        {fileErrorMessage}
                    </Typography>
                )}

                <Typography>
                    When your profile is matched, ServiceNow will email you a
                    confirmation.
                </Typography>

                <Box>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={submitting}
                    >
                        Submit request
                    </Button>
                </Box>
            </form>
        </Box>
    );
};
